import { getSubscriptionApiClient } from "../../shared/apis/sidekick/api-factories";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { toast } from "@shared/services/toast";
import { appLogger } from "@shared/services/app-logger";
import { buildAdminGetUserSubscriptionStatusQueryKey } from "./useAdminGetUserSubscription";

type Props = {
  onSuccess?: () => void;
};

type Params = {
  userId: string;
  trialDays: number;
};

export const useAdminRestoreUserSubscription = (props?: Props) => {
  const { onSuccess } = props || {};
  const api = getSubscriptionApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["admin-subscription", "restore"],
    mutationFn: async ({ userId, trialDays }: Params) => {
      const response =
        await api.subscriptionControllerAdminDeleteUserSubscriptionCancellation(
          {
            userId,
            restoreSubscriptionRequestBody: {
              trialDays,
            },
          },
        );
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: buildAdminGetUserSubscriptionStatusQueryKey({ userId }),
        });
      }, 1000);
      toast.success(
        `User subscription has been successfully restored with ${trialDays} trial days.`,
      );
      onSuccess?.();
      return response;
    },
    onError: async (error, { userId }) => {
      const apiError = await apiResponseErrorHandler(error);
      if (apiError.status >= 400 && apiError.status < 500) {
        toast.error(apiError.errors[0]);
        return;
      }
      toast.error(
        `Failed to restore user subscription with user ID ${userId}. If the issue persists, please contact support.`,
      );
      appLogger.error(`Failed to restore subscription`, { error });
    },
  });

  return mutation;
};
